import { data } from 'react-router';

import type { LoaderFunctionArgs } from '~/types/route-args';
import type {
    GetProjectBuilderModulesQuery,
    GetProjectBuilderModulesQueryVariables,
} from 'generated/graphql';
import { GetProjectBuilderModulesDocument } from 'generated/documents/GetProjectBuilderModulesDocument';
import { imageSourceFile } from '@modules/images/utils/image-source-file';
import { getPercentageValue, getPixelValue } from '~/utils/math';
import type { PreviewProductTag } from '~/ui-rocco/pages/directory-browse/ProjectImagePreview';
import type { MeasurementSystem } from '@archipro-website/localisation';
import {
    restatedPriceUnitLabel,
    toDisplayUnitFigure,
} from '@modules/product/util/measured-price';

/** Resource-route path for the Spaces / Styles preview's product rail. */
export const PROJECT_IMAGE_TAGS_ROUTE = '/remix-api/projects/image-tags';

/** Every tagged image in one project, keyed by its stored file path. */
export type ProjectImageTags = Record<string, PreviewProductTag[]>;

/** One photograph of the project's page, as the image viewer pages through it. */
export interface ProjectGalleryImage {
    /** The stored file, which is also the key its tags are filed under. */
    source: string;
    src: string;
    /**
     * The LibraryImage behind the photograph. A save from the viewer pins the
     * picture itself, so the viewer needs the id of whichever one it has paged
     * to rather than the one the reader first clicked.
     */
    id?: number;
    /**
     * What the photograph shows, where the library image names it. Each
     * picture describes itself: the viewer pages through the whole project,
     * and one description repeated down a gallery tells a screen reader
     * nothing about the photograph in front of it.
     */
    alt?: string;
}

/** The crops a gallery photograph is read from, largest first. */
const GALLERY_CROPS = ['FrontendDetailPage', 'DetailPageStandard'] as const;

/**
 * The only link shape this route will look up: `/project/<slug>`, one segment,
 * a trailing slash allowed.
 *
 * The route is public and unauthenticated, and a lookup walks a whole
 * page-builder response twice, so the parameter is checked for the shape it is
 * meant to have rather than only for its prefix.
 */
const PROJECT_LINK = /^\/project\/[a-z0-9]+(?:[-_.][a-z0-9]+)*\/?$/i;

/** Past this no project URL segment is real, so nothing is fetched for one. */
const MAX_LINK_LENGTH = 160;

/** Nothing to give back: an unusable link, or a lookup that failed. */
const emptyResult = (link: string) => ({
    link,
    tags: {} satisfies ProjectImageTags,
    images: [] as ProjectGalleryImage[],
});

/**
 * Products tagged in a project's images, for the Spaces / Styles preview's
 * product rail.
 *
 * One request per project, not per image: opening the preview already narrows
 * to a single project, and the page builder hands back every image's markers in
 * the same payload. This is the query the project detail page runs, so Apollo
 * usually has it cached by the time a reader opens a preview.
 *
 * Keyed by stored file path rather than by LibraryImage id — see
 * `imageSourceFile` for why the ids on either side do not line up.
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const link = url.searchParams.get('link') ?? '';

    // A project link is a same-site path of a known shape; anything else is
    // not ours to fetch, and is refused before any query runs.
    if (link.length > MAX_LINK_LENGTH || !PROJECT_LINK.test(link)) {
        return data(emptyResult(link.slice(0, MAX_LINK_LENGTH)), {
            status: 400,
        });
    }

    try {
        const result = await context.client.query<
            GetProjectBuilderModulesQuery,
            GetProjectBuilderModulesQueryVariables
        >({
            query: GetProjectBuilderModulesDocument,
            variables: { link, skipCache: false, editIntent: false },
        });
        return {
            link,
            tags: collectTags(
                result.data,
                context.localisation.countryConfig.measurementSystem,
            ),
            images: collectImages(result.data),
        };
    } catch (error) {
        context.logger.error('Error loading project image tags', {
            error,
            link,
        });
        return emptyResult(link);
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const asString = (value: unknown): string =>
    typeof value === 'string' ? value : '';

const asNumber = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/**
 * A marker's stored position, re-projected onto the picture as rendered.
 *
 * Markers are recorded as percentages of the *uncropped* image, so on any
 * cropped rendering — which every detail-page crop is — the raw percentages
 * land off the mark. Convert to pixels on the uncropped image, subtract the
 * crop origin, and take the percentage of the cropped box, which is the frame
 * the reader is actually looking at.
 *
 * Returns nothing when the image kept no crop box, or when the marker falls
 * outside the crop: that product is not visible in this rendering, so there is
 * nowhere honest to put a mark.
 */
const positionInCrop = (
    marker: Record<string, unknown>,
    image: Record<string, unknown>,
): { positionX: number; positionY: number } | undefined => {
    const posX = asNumber(marker.PositionX);
    const posY = asNumber(marker.PositionY);
    const widthBefore = asNumber(image.WidthBeforeCrop);
    const heightBefore = asNumber(image.HeightBeforeCrop);
    const widthAfter = asNumber(image.WidthAfterCrop);
    const heightAfter = asNumber(image.HeightAfterCrop);
    if (
        posX === undefined ||
        posY === undefined ||
        !widthBefore ||
        !heightBefore ||
        !widthAfter ||
        !heightAfter
    ) {
        return undefined;
    }

    const x = getPercentageValue(
        getPixelValue(posX, widthBefore) - (asNumber(image.CropPosX) ?? 0),
        widthAfter,
    );
    const y = getPercentageValue(
        getPixelValue(posY, heightBefore) - (asNumber(image.CropPosY) ?? 0),
        heightAfter,
    );
    if (x < 0 || x > 100 || y < 0 || y > 100) return undefined;

    return { positionX: x, positionY: y };
};

/** Every rendered URL hanging off a LibraryImage's file, however nested. */
const fileUrlsOf = (image: Record<string, unknown>): string[] => {
    const file = image.LibraryImageFile;
    if (!isRecord(file)) return [];
    const urls: string[] = [];
    for (const value of Object.values(file)) {
        if (typeof value === 'string') urls.push(value);
        else if (isRecord(value)) {
            for (const nested of Object.values(value)) {
                if (typeof nested === 'string') urls.push(nested);
            }
        }
    }
    return urls;
};

/**
 * The desktop rendition of a page image, or undefined without one. The crops
 * the page-builder fragment carries hold a `Desktop` and a `Mobile` rendition;
 * the viewer raises the width it asks for when it draws the picture.
 */
const gallerySrcOf = (image: Record<string, unknown>): string | undefined => {
    const file = image.LibraryImageFile;
    if (!isRecord(file)) return undefined;
    for (const crop of GALLERY_CROPS) {
        const rendition = file[crop];
        if (!isRecord(rendition)) continue;
        const src = asString(rendition.Desktop);
        if (src) return src;
    }
    return undefined;
};

/** What the library image calls the photograph: its caption, else its title. */
const galleryAltOf = (image: Record<string, unknown>): string | undefined =>
    asString(image.Caption).trim() || asString(image.Title).trim() || undefined;

/**
 * Every photograph on the project's page, in page order and once per stored
 * file, so the viewer can page through the whole project rather than the few
 * photos a filtered wall holds. The tagged products' own pictures hang off the
 * markers and are not the project's, so the walk does not go into them.
 */
const collectImages = (data: unknown): ProjectGalleryImage[] => {
    const images: ProjectGalleryImage[] = [];
    const seenNodes = new Set<unknown>();
    const seenFiles = new Set<string>();

    const walk = (node: unknown): void => {
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }
        if (!isRecord(node) || seenNodes.has(node)) return;
        seenNodes.add(node);

        const src = gallerySrcOf(node);
        const source = src ? imageSourceFile(src) : '';
        if (src && source && !seenFiles.has(source)) {
            seenFiles.add(source);
            const alt = galleryAltOf(node);
            const id = asNumber(node.ID);
            images.push({
                source,
                src,
                ...(id ? { id } : {}),
                ...(alt ? { alt } : {}),
            });
        }

        for (const [key, value] of Object.entries(node)) {
            if (key !== 'LibraryImageMarkers') walk(value);
        }
    };

    walk(data);
    return images;
};

/**
 * Walk the module tree for each image's tagged products.
 *
 * The markers hang off the page-builder module tree, whose shape varies by
 * module type, so this searches for images rather than hard-coding a path that
 * a page-builder change would silently break.
 */
const collectTags = (
    data: unknown,
    measurementSystem: MeasurementSystem,
): ProjectImageTags => {
    const tags: ProjectImageTags = {};
    const seen = new Set<unknown>();

    const walk = (node: unknown): void => {
        if (!isRecord(node) || seen.has(node)) return;
        seen.add(node);

        const file = node.LibraryImageFile;
        if (isRecord(file)) {
            const source = fileUrlsOf(node)
                .map(imageSourceFile)
                .find((candidate) => !!candidate);
            if (source) {
                const markers = node.LibraryImageMarkers;
                if (Array.isArray(markers) && markers.length) {
                    const entries = dedupeByProduct(
                        markers
                            .map((marker) =>
                                toTag(marker, node, measurementSystem),
                            )
                            .filter((tag): tag is PreviewProductTag => !!tag),
                    );
                    if (entries.length) tags[source] = entries;
                }
            }
        }

        for (const value of Object.values(node)) {
            if (Array.isArray(value)) value.forEach(walk);
            else walk(value);
        }
    };

    walk(data);
    return tags;
};

/**
 * One entry per product. A product tagged at several points on the one photo
 * carries a marker per point, which the rail has no way to tell apart.
 *
 * A point that survived the crop wins over one that did not, so the kept entry
 * can still be marked on the picture.
 */
const dedupeByProduct = (tags: PreviewProductTag[]): PreviewProductTag[] => {
    const byId = new Map<number, PreviewProductTag>();
    for (const tag of tags) {
        const kept = byId.get(tag.id);
        if (
            !kept ||
            (kept.positionX === undefined && tag.positionX !== undefined)
        ) {
            byId.set(tag.id, tag);
        }
    }
    return [...byId.values()];
};

/** One marker → a rail entry, skipping markers with no on-platform product. */
const toTag = (
    marker: unknown,
    image: Record<string, unknown>,
    measurementSystem: MeasurementSystem,
): PreviewProductTag | null => {
    if (!isRecord(marker) || !isRecord(marker.Product)) return null;
    const product = marker.Product;
    const professional = isRecord(product.Professional)
        ? product.Professional
        : undefined;
    const id = Number(product.ID);
    if (!Number.isInteger(id)) return null;

    return {
        id,
        title: asString(product.Title),
        professional: asString(professional?.Title),
        link: asString(product.Link),
        imageUrl: thumbnailOf(product),
        priceLabel: priceLabelOf(product, measurementSystem),
        ...positionInCrop(marker, image),
    };
};

/**
 * The product's tile thumbnail. `DefaultProductThumbnail` aliases the file to
 * `Thumbnail` and its sizes to lowercase `desktop` / `mobile`.
 */
const thumbnailOf = (product: Record<string, unknown>): string | undefined => {
    const thumbnail = product.Thumbnail;
    if (!isRecord(thumbnail)) return undefined;
    return (
        asString(thumbnail.desktop) || asString(thumbnail.mobile) || undefined
    );
};

/**
 * "FROM $1,599", as the design prints it. `ProductTilePricing` splits that into
 * a `Prefix` ("From") and the formatted `Text`; the design sets it uppercase.
 * On imperial markets a price per square, linear or cubic metre is restated in
 * the market's unit and labelled with it, the unit kept in its own case:
 * "FROM $4.65 / sq ft".
 */
const priceLabelOf = (
    product: Record<string, unknown>,
    measurementSystem: MeasurementSystem,
): string | undefined => {
    const pricing = product.PricingDisplay;
    if (!isRecord(pricing)) return undefined;
    const text = asString(pricing.Text);
    if (!text) return undefined;
    const prefix = asString(pricing.Prefix);
    const priceUnit = isRecord(pricing.ReferenceVariant)
        ? asString(pricing.ReferenceVariant.PriceUnit)
        : '';
    const figure = toDisplayUnitFigure(text, priceUnit, measurementSystem);
    const unitLabel = restatedPriceUnitLabel(priceUnit, measurementSystem);
    const label = `${prefix ? `${prefix} ` : ''}${figure}`.toUpperCase();
    return unitLabel ? `${label} / ${unitLabel}` : label;
};
